import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Spinner, Badge, Accordion, Table, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import Breadcrumbs from '../../components/breadcrumbs';
import { FaCheck, FaTimes, FaExternalLinkAlt, FaChevronLeft, FaChevronRight, FaInfoCircle, FaChevronDown, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';

const STEPS = ['Course Info', 'Find Standards', 'Confirm', 'Select Standards', 'Alignment', 'Outline'];
const COUNTRIES = ['USA', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Spain', 'Mexico', 'Brazil', 'Japan', 'China', 'India', 'Other'];
const GRADES = ['6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade'];

const StandardsWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).currentStep : 0;
  });
  const [loading, setLoading] = useState(false);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [courseId, setCourseId] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).courseId : null;
  });
  
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).formData : {
      courseTitle: '',
      courseDescription: '',
      country: 'USA',
      gradeLevel: '12th Grade',
      duration: 'Single Semester',
      credits: null,
      unitCount: 5,
      lessonCount: 5,
    };
  });

  const [standardsBodies, setStandardsBodies] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).standardsBodies : [];
  });
  const [selectedBodies, setSelectedBodies] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).selectedBodies : [];
  });
  const [individualStandards, setIndividualStandards] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).individualStandards : {};
  });
  const [selectedStandards, setSelectedStandards] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).selectedStandards : [];
  });
  const [alignmentData, setAlignmentData] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).alignmentData : null;
  });
  const [outline, setOutline] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).outline : null;
  });
  const [modificationPrompt, setModificationPrompt] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStandardDetail, setSelectedStandardDetail] = useState(null);
  const [expandedBody, setExpandedBody] = useState([]);
  const [loadingBodies, setLoadingBodies] = useState({});
  const [pollingOutline, setPollingOutline] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).pollingOutline || false : false;
  });
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationBodies, setGenerationBodies] = useState([]);
  const [pollingGeneration, setPollingGeneration] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).pollingGeneration || false : false;
  });
  const [alignmentStatus, setAlignmentStatus] = useState(null);
  const [alignmentError, setAlignmentError] = useState(null);
  const [pollingAlignment, setPollingAlignment] = useState(() => {
    const saved = localStorage.getItem('wizardState');
    return saved ? JSON.parse(saved).pollingAlignment || false : false;
  });
  const [outlineError, setOutlineError] = useState(null);

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.courseTitle.trim() && formData.courseDescription.trim();
      case 1:
        return !loading && selectedBodies.length > 0;
      case 2:
        return selectedBodies.length > 0;
      case 3:
        return !pollingGeneration && selectedStandards.length > 0;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (courseId) {
        await updateCourse();
      } else {
        await createCourse();
      }
    } else if (currentStep === 1) {
      await updateStandardsBodies();
    } else if (currentStep === 2) {
      await startStandardsGeneration();
    } else if (currentStep === 3) {
      await saveSelectedStandards();
    } else if (currentStep === 4) {
      await generateOutline();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const createCourse = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/course`, {
        user_id: parseInt(userId),
        course_title: formData.courseTitle,
        course_description: formData.courseDescription,
        country: formData.country,
        grade_level: formData.gradeLevel,
        duration: formData.duration,
        unit_count: formData.unitCount,
        lesson_count: formData.lessonCount,
      });

      if (response.data.success) {
        setCourseId(response.data.data.course_id);
        setFormData(prev => ({ ...prev, credits: response.data.data.credits }));
        await discoverStandards(response.data.data.course_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const updateCourse = async () => {
    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/standards-wizard/course`, {
        course_id: courseId,
        course_title: formData.courseTitle,
        course_description: formData.courseDescription,
        country: formData.country,
        grade_level: formData.gradeLevel,
        duration: formData.duration,
        unit_count: formData.unitCount,
        lesson_count: formData.lessonCount,
      });
      await discoverStandards(courseId);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  const discoverStandards = async (id) => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/discovery`, {
        course_id: id || courseId,
      });

      if (response.data.success) {
        const bodies = response.data.data.standards_bodies || [];
        setStandardsBodies(bodies);
        setSelectedBodies(bodies.filter(b => b.is_selected).map(b => b.id));
        setCurrentStep(1);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to discover standards');
    } finally {
      setLoading(false);
    }
  };

  const updateStandardsBodies = async () => {
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/selection`, {
        course_id: courseId,
        standards_body_ids: selectedBodies,
      });
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update selection');
    } finally {
      setLoading(false);
    }
  };

  const startStandardsGeneration = async () => {
    setLoading(true);
    setGenerationStatus('PENDING');
    setGenerationBodies([]);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/generate-standards`, {
        course_id: courseId,
      });
      setPollingGeneration(true);
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start standards generation');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndividualStandards = async (bodyId) => {
    if (individualStandards[bodyId]) return;
    
    setLoadingBodies(prev => ({ ...prev, [bodyId]: true }));
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/selection`, {
        params: { course_id: courseId, standards_body_id: bodyId },
      });

      if (response.data.success) {
        const standards = response.data.data.standards || [];
      setIndividualStandards(prev => ({ ...prev, [bodyId]: standards }));
      const newIds = standards.map(s => s.id);
      setSelectedStandards(prev => [...new Set([...prev, ...newIds])]);

      }
    } catch (error) {
      toast.error('Failed to fetch standards');
    } finally {
      setLoadingBodies(prev => ({ ...prev, [bodyId]: false }));
    }
  };

  const saveSelectedStandards = async () => {
    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/standards-wizard/selection`, {
        course_id: courseId,
        selected_standard_ids: selectedStandards,
      });
      await startAlignment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save standards');
    } finally {
      setLoading(false);
    }
  };

  const startAlignment = async () => {
    setLoading(true);
    setAlignmentData(null);
    setAlignmentStatus('PENDING');
    setAlignmentError(null);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/alignment`, {
        course_id: courseId,
      });
      setPollingAlignment(true);
      setCurrentStep(4);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start alignment generation');
    } finally {
      setLoading(false);
    }
  };

  const generateOutline = async () => {
    setLoading(true);
    setOutlineError(null);
    setPollingOutline(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/standards-wizard/outline`, {
        course_id: courseId,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate outline');
      setPollingOutline(false);
    } finally {
      setLoading(false);
    }
  };

  const retryOutline = () => {
    generateOutline();
  };

  const modifyOutline = async () => {
    if (!modificationPrompt.trim()) return;

    setModifyLoading(true);
    setOutlineError(null);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/standards-wizard/outline`, {
        course_id: courseId,
        modification_request: modificationPrompt,
      });
      setModificationPrompt('');
      setPollingOutline(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to modify outline');
      setModifyLoading(false);
    }
  };

  const completeCourse = async () => {
    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/standards-wizard/course`, {
        course_id: courseId,
        status: 'COMPLETED',
      });
      localStorage.removeItem('wizardState');
      toast.success('Course completed successfully');
      navigate('/home');
    } catch (error) {
      toast.error('Failed to complete course');
    } finally {
      setLoading(false);
    }
  };

  const fetchStandardDetail = async (standardId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/standard-detail`, {
        params: { standard_id: standardId },
      });

      if (response.data.success) {
        setSelectedStandardDetail(response.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch standard details');
    }
  };

  useEffect(() => {
    const state = {
      currentStep,
      courseId,
      formData,
      standardsBodies,
      selectedBodies,
      individualStandards,
      selectedStandards,
      alignmentData,
      outline,
      pollingOutline,
      pollingGeneration,
      pollingAlignment
    };
    localStorage.setItem('wizardState', JSON.stringify(state));
  }, [currentStep, courseId, formData, standardsBodies, selectedBodies, individualStandards, selectedStandards, alignmentData, outline, pollingOutline, pollingGeneration, pollingAlignment]);

  useEffect(() => {
    if (currentStep !== 3 || !courseId || !pollingGeneration) return;

    const pollGeneration = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/generate-standards`, {
          params: { course_id: courseId },
        });
        if (response.data.success) {
          const { overall_status, bodies } = response.data.data;
          setGenerationStatus(overall_status);
          setGenerationBodies(bodies || []);

          (bodies || []).forEach(b => {
            if (b.status === 'COMPLETED') fetchIndividualStandards(b.standards_body_id);
          });

          if (['COMPLETED', 'PARTIAL', 'FAILED'].includes(overall_status)) {
            setPollingGeneration(false);
            if (overall_status === 'FAILED') {
              toast.error('Standards generation failed');
            } else if (overall_status === 'PARTIAL') {
              toast.warn('Some standards bodies failed to generate');
            }
          }
        }
      } catch (error) {
        // Continue polling
      }
    };

    const interval = setInterval(pollGeneration, 3000);
    pollGeneration();

    return () => clearInterval(interval);
  }, [currentStep, courseId, pollingGeneration]);

  const retryStandardsGeneration = () => {
    startStandardsGeneration();
  };

  useEffect(() => {
    const allIds = Object.values(individualStandards).flat().map(s => s.id);
    if (allIds.length > 0) {
      setSelectedStandards(prev => [...new Set([...prev, ...allIds])]);
    }
  }, [individualStandards]);

  useEffect(() => {
    if (currentStep !== 4 || !courseId || !pollingAlignment) return;

    const pollAlignment = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/alignment`, {
          params: { course_id: courseId },
        });
        if (response.data.success) {
          const { status, error, alignment } = response.data.data;
          setAlignmentStatus(status);
          if (status === 'COMPLETED') {
            setAlignmentData(alignment);
            setPollingAlignment(false);
          } else if (status === 'FAILED') {
            setAlignmentError(error || 'Alignment generation failed');
            setPollingAlignment(false);
            toast.error(error || 'Alignment generation failed');
          }
        }
      } catch (error) {
        // Continue polling
      }
    };

    const interval = setInterval(pollAlignment, 3000);
    pollAlignment();

    return () => clearInterval(interval);
  }, [currentStep, courseId, pollingAlignment]);

  const retryAlignment = () => {
    startAlignment();
  };

  useEffect(() => {
    if ((currentStep !== 4 && currentStep !== 5) || !courseId || !pollingOutline) return;

    const pollOutline = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/standards-wizard/outline`, {
          params: { course_id: courseId },
        });
        if (response.data.success) {
          const { status } = response.data.data;
          if (status === 'COMPLETED') {
            setOutline(response.data.data);
            setPollingOutline(false);
            setModifyLoading(false);
            setLoading(false);
            setCurrentStep(5);
          } else if (status === 'FAILED') {
            setOutlineError(response.data.data.error || 'Outline generation failed');
            setPollingOutline(false);
            setModifyLoading(false);
            setLoading(false);
            toast.error(response.data.data.error || 'Outline generation failed');
          }
        }
      } catch (error) {
        // Continue polling
      }
    };

    const interval = setInterval(pollOutline, 3000);
    pollOutline();

    return () => clearInterval(interval);
  }, [currentStep, courseId, pollingOutline]);

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step1CourseInfo formData={formData} setFormData={setFormData} />;
      case 1:
        return <Step2Discovery standardsBodies={standardsBodies} selectedBodies={selectedBodies} setSelectedBodies={setSelectedBodies} loading={loading} />;
      case 2:
        return <Step3Confirm formData={formData} standardsBodies={standardsBodies.filter(b => selectedBodies.includes(b.id))} />;
      case 3:
        return <Step4SelectStandards standardsBodies={standardsBodies.filter(b => selectedBodies.includes(b.id))} individualStandards={individualStandards} selectedStandards={selectedStandards} setSelectedStandards={setSelectedStandards} fetchStandardDetail={fetchStandardDetail} expandedBody={expandedBody} setExpandedBody={setExpandedBody} loadingBodies={loadingBodies} generationStatus={generationStatus} generationBodies={generationBodies} pollingGeneration={pollingGeneration} onRetryGeneration={retryStandardsGeneration} />;
      case 4:
        return <Step5Alignment alignmentData={alignmentData} standardsBodies={standardsBodies.filter(b => selectedBodies.includes(b.id))} alignmentStatus={alignmentStatus} alignmentError={alignmentError} pollingAlignment={pollingAlignment} onRetryAlignment={retryAlignment} />;
      case 5:
        return <Step6Outline outline={outline} modificationPrompt={modificationPrompt} setModificationPrompt={setModificationPrompt} modifyOutline={modifyOutline} modifyLoading={modifyLoading} pollingOutline={pollingOutline} outlineError={outlineError} onRetryOutline={retryOutline} />;
      default:
        return null;
    }
  };

  return (
    <Container fluid className="p-4">
      <Breadcrumbs />
      <p className="section-title mb-4">Standards Wizard</p>

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            {STEPS.map((step, index) => (
              <React.Fragment key={index}>
                <div className="d-flex align-items-center">
                  <div className={`rounded-circle d-flex align-items-center justify-content-center ${currentStep >= index ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px', fontWeight: 'bold' }}>
                    {currentStep > index ? <FaCheck /> : index + 1}
                  </div>
                  <span className={`ms-2 ${currentStep === index ? 'fw-bold text-primary' : 'text-muted'}`}>{step}</span>
                </div>
                {index < STEPS.length - 1 && <div className="flex-grow-1 mx-2" style={{ height: '2px', backgroundColor: currentStep > index ? '#2e65ae' : '#dee2e6' }} />}
              </React.Fragment>
            ))}
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="bg-white p-4 rounded shadow-sm d-flex flex-column" style={{ height: 'calc(100vh - 280px)', overflow: 'auto' }}>
            {renderStep()}
          </div>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setCurrentStep(prev => prev - 1)} disabled={currentStep === 0 || loading}>
            <FaChevronLeft className="me-1" /> Back
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button variant="primary" onClick={handleNext} disabled={!canProceed() || loading || pollingOutline || pollingGeneration || pollingAlignment}>
              {(loading || pollingOutline || pollingGeneration || pollingAlignment) ? <Spinner animation="border" size="sm" className="me-1" /> : null}
              Continue <FaChevronRight className="ms-1" />
            </Button>
          ) : (
            <Button variant="success" onClick={completeCourse} disabled={loading || modifyLoading}>
              {loading ? <Spinner animation="border" size="sm" className="me-1" /> : <FaCheck className="me-1" />}
              Complete & Save
            </Button>
          )}
        </Col>
      </Row>

      <StandardDetailModal show={showDetailModal} onHide={() => setShowDetailModal(false)} standard={selectedStandardDetail} />
    </Container>
  );
};

const Step1CourseInfo = ({ formData, setFormData }) => (
  <div className="text-center">
    <div className="mb-4">
      <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '60px', height: '60px' }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0d6efd" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      </div>
    </div>
    <h3 className="mb-2">Describe Your Course</h3>
    <p className="text-muted mb-4">Tell us about the course you're building</p>
    <Form className="text-start" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <Form.Group className="mb-3">
        <Form.Label>Course Title</Form.Label>
        <Form.Control type="text" placeholder="e.g., Geometry Part 2, Spanish 3, Chemistry" value={formData.courseTitle} onChange={(e) => setFormData({ ...formData, courseTitle: e.target.value })} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Course Description</Form.Label>
        <Form.Control as="textarea" rows={4} placeholder="Describe the course content and learning objectives..." value={formData.courseDescription} onChange={(e) => setFormData({ ...formData, courseDescription: e.target.value })} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label><span className="me-1">🌍</span>Country</Form.Label>
        <Form.Select style={{ color: "black" }} value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label><span className="me-1">🎓</span>Grade Level</Form.Label>
        <Form.Select style={{ color: "black" }} value={formData.gradeLevel} onChange={(e) => setFormData({ ...formData, gradeLevel: e.target.value })}>
          {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label><span className="me-1">📅</span>Duration</Form.Label>
        <Form.Select style={{ color: "black" }} value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })}>
          <option value="Single Semester">Single Semester</option>
          <option value="Full Year">Full Year</option>
        </Form.Select>
      </Form.Group>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Unit Count</Form.Label>
            <Form.Control type="number" min="1" max="20" value={formData.unitCount} onChange={(e) => setFormData({ ...formData, unitCount: parseInt(e.target.value) })} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Lessons per Unit</Form.Label>
            <Form.Control type="number" min="1" max="20" value={formData.lessonCount} onChange={(e) => setFormData({ ...formData, lessonCount: parseInt(e.target.value) })} />
          </Form.Group>
        </Col>
      </Row>
      {formData.duration === 'Single Semester' && ['8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade'].includes(formData.gradeLevel) && (
        <div className="alert alert-info d-flex align-items-start">
          <FaInfoCircle className="me-2 mt-1" />
          <div>
            <strong>Credit Information</strong>
            <div className="small">Single-semester course for grades 8-12 corresponds to <strong>0.5 Carnegie credits</strong> and <strong>5 credits in California</strong></div>
          </div>
        </div>
      )}
    </Form>
  </div>
);

const Step2Discovery = ({ standardsBodies, selectedBodies, setSelectedBodies, loading }) => {
  const toggleBody = (id) => {
    setSelectedBodies(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };

  return (
    <div className="text-center">
      <div className="mb-4">
        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '60px', height: '60px' }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0d6efd" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>
      </div>
      <h3 className="mb-2">Finding Relevant Standards</h3>
      <p className="text-muted mb-4">AI is identifying applicable standards bodies for your course</p>
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Analyzing course content and discovering relevant standards...</p>
        </div>
      ) : (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="alert alert-info d-flex align-items-center mb-4">
            <FaCheck className="me-2" />
            <span>AI validation complete (verified by secondary AI)</span>
          </div>
          <p className="text-start mb-3">Select the standards bodies you want to include in your course:</p>
          <div className="text-start">
            {standardsBodies.map(body => (
              <div key={body.id} className={`p-3 mb-2 border rounded ${selectedBodies.includes(body.id) ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'}`} style={{ cursor: 'pointer' }} onClick={() => toggleBody(body.id)}>
                <div className="d-flex align-items-start">
                  <Form.Check type="checkbox" checked={selectedBodies.includes(body.id)}  className="me-2" />
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <strong>{body.name} {body.abbreviation && `(${body.abbreviation})`}</strong>
                      {body.relevance_score && <Badge bg="success" className="ms-2">{body.relevance_score}% Relevant</Badge>}
                    </div>
                    {body.subject_area && <div className="small text-muted mb-1"><strong>Subject:</strong> {body.subject_area}</div>}
                    {body.reason && <div className="small text-muted mb-1"><strong>Reason:</strong> {body.reason}</div>}
                    {body.website_url && (
                      <div className="small">
                        <a href={body.website_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
                          <FaExternalLinkAlt className="me-1" style={{ fontSize: '10px' }} /> Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Step3Confirm = ({ formData, standardsBodies }) => (
  <div className="text-center">
    <div className="mb-4">
      <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '60px', height: '60px' }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0d6efd" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      </div>
    </div>
    <h3 className="mb-2">Confirm Standards Bodies</h3>
    <p className="text-muted mb-4">Review your selected standards bodies before proceeding</p>
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div className="bg-light p-3 rounded mb-4 text-start">
        <h6 className="text-primary mb-3">Course Summary</h6>
        <Row>
          <Col md={6}>
            <div className="mb-2"><span className="text-muted">Title:</span> <strong>{formData.courseTitle}</strong></div>
            <div className="mb-2"><span className="text-muted">Grade:</span> <strong>{formData.gradeLevel}</strong></div>
          </Col>
          <Col md={6}>
            <div className="mb-2"><span className="text-muted">Country:</span> <strong>{formData.country}</strong></div>
            <div className="mb-2"><span className="text-muted">Duration:</span> <strong>{formData.duration}</strong></div>
          </Col>
        </Row>
      </div>
      <div className="text-start">
        <h6 className="text-primary mb-3">Selected Standards Bodies ({standardsBodies.length})</h6>
        {standardsBodies.map((body, idx) => (
          <div key={body.id} className="p-3 mb-2 bg-primary bg-opacity-10 rounded">
            <div className="d-flex align-items-center mb-2">
              <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" style={{ width: '30px', height: '30px', fontSize: '14px', fontWeight: 'bold' }}>
                {idx + 1}
              </div>
              <div className="flex-grow-1">
                <strong>{body.name} {body.abbreviation && `(${body.abbreviation})`}</strong>
                {body.relevance_score && <Badge bg="success" className="ms-2">{body.relevance_score}% Relevant</Badge>}
              </div>
            </div>
            {body.subject_area && <div className="small text-muted mb-1"><strong>Subject:</strong> {body.subject_area}</div>}
            {body.reason && <div className="small text-muted mb-1"><strong>Reason:</strong> {body.reason}</div>}
            {body.website_url && (
              <div className="small">
                <a href={body.website_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                  <FaExternalLinkAlt className="me-1" style={{ fontSize: '10px' }} /> Visit Website
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="alert alert-warning d-flex align-items-start mt-4 text-start">
        <FaInfoCircle className="me-2 mt-1" style={{ flexShrink: 0 }} />
        <small>You can go back to modify your selections. Proceeding will fetch the individual standards for each selected body.</small>
      </div>
    </div>
  </div>
);

const Step4SelectStandards = ({ standardsBodies, individualStandards, selectedStandards, setSelectedStandards, fetchStandardDetail, expandedBody, setExpandedBody, loadingBodies, generationStatus, generationBodies, pollingGeneration, onRetryGeneration }) => {
  const toggleStandard = (id) => {
    setSelectedStandards(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const statusVariant = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'primary';
      case 'FAILED': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <div className="text-center">
      <div className="mb-4">
        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '60px', height: '60px' }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0d6efd" strokeWidth="2">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
        </div>
      </div>
      <h3 className="mb-2">Review Individual Standards</h3>
      <p className="text-muted mb-4">Select the specific standards to cover in your course</p>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {(pollingGeneration || ['PARTIAL', 'FAILED'].includes(generationStatus)) && generationBodies.length > 0 && (
          <div className={`alert alert-${generationStatus === 'FAILED' ? 'danger' : generationStatus === 'PARTIAL' ? 'warning' : 'info'} text-start mb-3`}>
            <div className="d-flex align-items-center mb-2">
              {pollingGeneration && <Spinner animation="border" size="sm" className="me-2" />}
              <strong>
                {pollingGeneration ? 'Generating standards...' : generationStatus === 'FAILED' ? 'Standards generation failed' : 'Some standards bodies failed'}
              </strong>
            </div>
            {generationBodies.map(b => (
              <div key={b.standards_body_id} className="d-flex justify-content-between align-items-center small py-1">
                <span>{b.name} {b.abbreviation && `(${b.abbreviation})`}</span>
                <Badge bg={statusVariant(b.status)}>{b.status}{b.error ? ` — ${b.error}` : ''}</Badge>
              </div>
            ))}
            {['PARTIAL', 'FAILED'].includes(generationStatus) && !pollingGeneration && (
              <Button variant="outline-primary" size="sm" className="mt-2" onClick={onRetryGeneration}>Retry</Button>
            )}
          </div>
        )}
        <Accordion alwaysOpen className="text-start">
          {standardsBodies.map(body => {
            const standards = individualStandards[body.id] || [];
            const selectedCount = standards.filter(s => selectedStandards.includes(s.id)).length;
            return (
              <Accordion.Item key={body.id} eventKey={body.id.toString()} className="mb-2">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div>
                      <span><strong>{body.name} {body.abbreviation && `(${body.abbreviation})`}</strong></span>
                      {body.relevance_score && <Badge bg="success" className="ms-2">{body.relevance_score}% Relevant</Badge>}
                    </div>
                    <Badge bg="primary" pill>{selectedCount}/{standards.length}</Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="mb-3 p-2 bg-light rounded">
                    {body.subject_area && <div className="small mb-1"><strong>Subject:</strong> {body.subject_area}</div>}
                    {body.reason && <div className="small mb-1"><strong>Reason:</strong> {body.reason}</div>}
                    {body.website_url && (
                      <div className="small">
                        <a href={body.website_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          <FaExternalLinkAlt className="me-1" style={{ fontSize: '10px' }} /> Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                  {loadingBodies[body.id] ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="text-muted small mt-2">Loading standards...</p>
                    </div>
                  ) : standards.map(std => (
                    <div key={std.id} className="border-bottom py-3">
                      <div className="d-flex align-items-start">
                        <Form.Check 
                          type="checkbox" 
                          checked={selectedStandards.includes(std.id)} 
                          onChange={() => toggleStandard(std.id)}
                          className="me-3"
                        />
                        <div className="flex-grow-1">
                          <div className="mb-1">
                            <Badge bg="secondary" className="me-2">{std.standard_code}</Badge>
                            <strong>{std.title}</strong>
                          </div>
                          <p className="text-muted small mb-2">{std.description}</p>
                          <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => fetchStandardDetail(std.id)}>
                            <FaExternalLinkAlt className="me-1" style={{ fontSize: '10px' }} /> View details & examples
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
};

const Step5Alignment = ({ alignmentData, standardsBodies, alignmentStatus, alignmentError, pollingAlignment, onRetryAlignment }) => {
  const tableRef = React.useRef(null);
  const [exporting, setExporting] = useState(null);

  if (alignmentStatus === 'FAILED' && !pollingAlignment) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger d-inline-block text-start" style={{ maxWidth: '600px' }}>
          <div className="d-flex align-items-start">
            <FaInfoCircle className="me-2 mt-1" style={{ flexShrink: 0 }} />
            <div>
              <strong>Alignment generation failed</strong>
              <div className="small mt-1">{alignmentError}</div>
            </div>
          </div>
          <Button variant="outline-primary" size="sm" className="mt-3" onClick={onRetryAlignment}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!alignmentData?.alignments) return (
    <div className="text-center py-5">
      <Spinner animation="border" />
      <p className="mt-3">Generating standards alignment...</p>
    </div>
  );

  const matchBody = (sBody, body) => {
    const a = (sBody || '').trim().toLowerCase();
    const abbr = (body.abbreviation || '').trim().toLowerCase();
    const name = (body.name || '').trim().toLowerCase();
    return a === abbr || a === name || a.includes(abbr) || abbr.includes(a) || a.includes(name) || name.includes(a);
  };

  const exportPdf = async () => {
    if (!tableRef.current) return;
    setExporting('pdf');
    try {
      await html2pdf().set({
        margin: [15, 10, 15, 10],
        filename: 'standards-alignment-chart.pdf',
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' },
      }).from(tableRef.current).save();
    } finally {
      setExporting(null);
    }
  };

  const exportExcel = () => {
    setExporting('excel');
    setTimeout(() => {
      try {
        const rows = alignmentData.alignments.map(alignment => {
          const row = { 'Standard Topic': alignment.topic };
          standardsBodies.forEach(body => {
            const std = alignment.standards?.find(s => matchBody(s.body, body));
            row[body.abbreviation || body.name] = std ? '✓' : '—';
          });
          return row;
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const colWidths = [{ wch: 30 }, ...standardsBodies.map(() => ({ wch: 15 }))];
        ws['!cols'] = colWidths;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Alignment');
        XLSX.writeFile(wb, 'standards-alignment-chart.xlsx');
      } finally {
        setExporting(null);
      }
    }, 300);
  };

  return (
    <div className="text-center">
      <div className="mb-4">
        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '60px', height: '60px' }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0d6efd" strokeWidth="2">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
        </div>
      </div>
      <h3 className="mb-2">Standards Alignment Chart</h3>
      <p className="text-muted mb-4">See how standards align across different bodies</p>
      <div className="d-flex justify-content-end gap-2 mb-3" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Button variant="outline-primary" size="sm" onClick={exportPdf} disabled={!!exporting}>
          {exporting === 'pdf' ? <Spinner animation="border" size="sm" className="me-1" /> : <FaFilePdf className="me-1" />}
          {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
        </Button>
        <Button variant="outline-success" size="sm" onClick={exportExcel} disabled={!!exporting}>
          {exporting === 'excel' ? <Spinner animation="border" size="sm" className="me-1" /> : <FaFileExcel className="me-1" />}
          {exporting === 'excel' ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>
      <div style={{ maxWidth: '900px', margin: '0 auto' }} ref={tableRef}>
        <Table responsive bordered className="text-start">
          <thead className="table-light">
            <tr>
              <th style={{ fontWeight: '600', color: '#0d6efd' }}>Standard Topic</th>
              {standardsBodies.map(body => <th key={body.id} className="text-center" style={{ fontWeight: '600', color: '#0d6efd', fontSize: '0.85rem' }}>{body.abbreviation || body.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {alignmentData.alignments.map((alignment, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: '500', color: '#0d6efd' }}>{alignment.topic}</td>
                {standardsBodies.map(body => {
                  const std = alignment.standards?.find(s => matchBody(s.body, body));
                  return (
                    <td key={body.id} className="text-center" style={{ backgroundColor: std ? '#e7f3ff' : '#f8f9fa' }}>
                      {std ? <FaCheck style={{ color: '#0d6efd' }} /> : <span style={{ color: '#dee2e6' }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="alert alert-info d-flex align-items-start mt-4 text-start">
          <FaInfoCircle className="me-2 mt-1" style={{ flexShrink: 0, color: '#0dcaf0' }} />
          <small><strong>Tip:</strong> Standards marked with ✓ across multiple bodies indicate core competencies that should be prioritized in your course design.</small>
        </div>
      </div>
    </div>
  );
};

const Step6Outline = ({ outline, modificationPrompt, setModificationPrompt, modifyOutline, modifyLoading, pollingOutline, outlineError, onRetryOutline }) => {
  const [expandedUnits, setExpandedUnits] = useState([]);

  const toggleUnit = (unitNumber) => {
    setExpandedUnits(prev => 
      prev.includes(unitNumber) ? prev.filter(u => u !== unitNumber) : [...prev, unitNumber]
    );
  };

  if (outlineError && !pollingOutline && !modifyLoading) return (
    <div className="text-center py-5">
      <div className="alert alert-danger d-inline-block text-start" style={{ maxWidth: '600px' }}>
        <div className="d-flex align-items-start">
          <FaInfoCircle className="me-2 mt-1" style={{ flexShrink: 0 }} />
          <div>
            <strong>Outline generation failed</strong>
            <div className="small mt-1">{outlineError}</div>
          </div>
        </div>
        <Button variant="outline-primary" size="sm" className="mt-3" onClick={onRetryOutline}>Retry</Button>
      </div>
    </div>
  );

  if (!outline?.units || outline.units.length === 0 || modifyLoading || pollingOutline) return (
    <div className="text-center py-5">
      <Spinner animation="border" />
      <p className="mt-3">{modifyLoading ? 'Modifying outline...' : 'Generating course outline...'}</p>
    </div>
  );

  return (
    <div>
      <h3 className="mb-4 text-center">Course Outline</h3>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {outline.units.map(unit => (
          <div key={unit.unit_number} className="mb-3 border rounded">
            <div 
              className="d-flex align-items-center justify-content-between p-3 bg-light" 
              style={{ cursor: 'pointer' }}
              onClick={() => toggleUnit(unit.unit_number)}
            >
              <div className="d-flex align-items-center flex-grow-1">
                {expandedUnits.includes(unit.unit_number) ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                <div>
                  <h6 className="mb-0">
                    <strong>Unit {unit.unit_number}: {unit.unit_title}</strong>
                  </h6>
                  {!expandedUnits.includes(unit.unit_number) && <small className="text-muted">{unit.unit_description}</small>}
                </div>
              </div>
              {unit.duration_weeks && <Badge bg="dark">Duration: {unit.duration_weeks} weeks</Badge>}
            </div>
            {expandedUnits.includes(unit.unit_number) && (
              <div className="p-3">
                <p className="text-muted mb-3">{unit.unit_description}</p>
                {unit.lessons?.map(lesson => (
                  <div key={lesson.lesson_number} className="border-top pt-3 mt-2">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-start gap-2">
                        <Badge bg="secondary">{unit.unit_number}.{lesson.lesson_number}</Badge>
                        <div>
                          <strong>{lesson.lesson_title}</strong>
                          <div className="text-muted small mb-1 mt-1" dangerouslySetInnerHTML={{ __html: lesson.lesson_description }} />
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-center gap-3">
                        {lesson.duration_days && <Badge bg="light" text="dark">Duration: {lesson.duration_days} days</Badge>}
                        {lesson.standards?.map(std => <Badge key={std.id} bg="info" className="ms-1">{std.code}</Badge>)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div className="mt-4">
          <Form.Label>Modify with AI</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control type="text" placeholder='e.g., "Switch Unit 2 and Unit 3"' value={modificationPrompt} onChange={(e) => setModificationPrompt(e.target.value)} />
            <Button onClick={modifyOutline} disabled={modifyLoading || !modificationPrompt.trim()}>
              {modifyLoading ? <Spinner animation="border" size="sm" /> : 'Apply'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StandardDetailModal = ({ show, onHide, standard }) => (
  <Modal show={show} onHide={onHide} size="lg" centered  className='mt-5'>
    <Modal.Header closeButton>
      <Modal.Title>{standard?.standard_code} - {standard?.title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {standard && (
        <>
          <p><strong>Description:</strong> {standard.description}</p>
          <p><strong>AI Explanation:</strong> {standard.full_definition}</p>
          {standard.examples?.length > 0 && (
            <>
              <strong>Examples:</strong>
              <ul>{standard.examples.map((ex, i) => <li key={i}>{ex}</li>)}</ul>
            </>
          )}
          {standard.cross_references?.length > 0 && (
            <>
              <strong>Cross References:</strong>
              <ul>{standard.cross_references.map((ref, i) => <li key={i}>{ref}</li>)}</ul>
            </>
          )}
        </>
      )}
    </Modal.Body>
  </Modal>
);

export default StandardsWizard;
